import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip, arrayMove} from "react-common-components";
import { useForm } from "react-hook-form";
import Loader from "src/components/Loader";
import SidebarCard from "src/components/SidebarCard";
import { ISidebarCardItem, IFiltersCard, IRefSidebarCard } from "src/components/SidebarCard/interface";
import { useTranslation } from "src/context/Translations/context";
import { IMetba001001, IRefMapsModals, ISidebarCardItemMaps } from "src/pages/StrategicPlanning/Maps/interface";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import SidebarCardFilters from "src/components/SidebarCard/sidebarCardFilters";
import ModalIniciatives from "src/components/ModalIniciatives";
import { IRefIniciativeModals } from "src/components/ModalIniciatives/interface";
import { useParams } from "react-router-dom";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { IIntbb001BudgetGrouper, IRefActionsModalBudget } from "src/pages/Actions/Panel/interface";
import ModalBudget from "src/pages/Actions/Panel/components/modalBudget";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import formatNumber from "src/utils/formatNumber";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import Title from "src/components/Title";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import SortableContext from "src/components/Sortable/Context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import DragOverlay from "src/components/Sortable/DragOverlay";
import WrapText from "src/components/WrapText";
import PerspectiveList from "./Components/Perspective";
import ModalsMap from "./modals";
import ModalPerspective from "./Components/Perspective/modals";
import ModalObjective from "./Components/Objectives/modals";
import MapContent from "./style";
import { IMetba002, IRefPerspectiveModals } from "./Components/Perspective/interface";
import { IMetba003, IRefObjectivesModals } from "./Components/Objectives/interface";
import BadgeAssociate from "./Components/Badge/badgeAssociate";
import TooltipGaugeChartMaps, { ITooltipGaugeChartMapsValue } from "./Components/Gauge";
import { MapContextProvider } from "./Components/MapsContextProvider";
import { ObjectiveCardBasic } from "./Components/Objectives/styles";
import SidebarMaps from "./Components/SidebarMaps";

const Maps: React.FC = () => {
	const params = useParams<{
        mapId: string;
    }>();
	
	/** HOOK */
	const refModalsMap = useRef<IRefMapsModals>(null);
	const refModalsPerspective = useRef<IRefPerspectiveModals>(null);
	const refModalsObjectives = useRef<IRefObjectivesModals>(null);
	const refModalActionBudget = useRef<IRefActionsModalBudget>(null);
	const refModalIniciatives = useRef<IRefIniciativeModals>(null);
	const refSidebarCard = useRef<IRefSidebarCard>(null);

	const {confirm} = useConfirmModal();
	const { addToast } = RCCHookToast();
	const { translate, currentLanguage } = useTranslation();
	const { setLoading: setLoadingBackdrop } = useLoaderBackdrop();
	const { administrator, superUser } = useAuth();

	const [cards, setCards] = useState<ISidebarCardItemMaps[]>([]);
	const [perspectives, setPerspectives] = useState<IMetba002[]>([]);
	const [objectives, setObjectives] = useState<IMetba003[]>([]);
	const [cardDragOver, setCardDragOver] = useState<string>();
	const [updatingPercentage, setUpdatingPercentage] = useState(false);
	const [mapSelected, setMapSelected] = useState<IMetba001001>({} as IMetba001001);

	const [openDetails, setOpenDetails] = useState(false);
	const [showCauseEffect, setShowCauseEffect] = useState(false);

	const [relateEffect1, setRelateEffect1] = useState<IMetba003>();
	const [relateEffect2, setRelateEffect2] = useState<IMetba003>();
	const [inEditCauseEffect, setInEditCauseEffect] = useState(false);

	const [tooltipValue, setTooltipValue] = useState<ITooltipGaugeChartMapsValue | undefined>(undefined);

	/** LOADING */
	const [loading, setLoading] = useState(true);
	const [errorLoadingMaps, setErrorLoadingMaps] = useState(false);
	const [loadingMapContent, setLoadingMapContent] = useState(false);
	const [errorLoadingMapContent, setErrorLoadingMapContent] = useState(false);

	/** FILTERS */
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersCard>({ 
		f: "", 
		startDate: null,
		endDate: null,
		active: "S",
		tags: ""
	});
	
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control:controlFilters, 
		reset: resetFilters 
	} = useForm();

	const mapsSubtitle = (dataInit:any, dataFinale:any) => {
		const dataInicio = formatDate(new Date(dataInit), currentLanguage);
		const dataFim = formatDate(new Date(dataFinale), currentLanguage);
		return `${dataInicio} ${translate("Até")} ${dataFim}`; 
	};

	const onMouseOverObjective = (objective: IMetba003) => {
		setTooltipValue({
			title: objective.mea003Nome,
			colorActions: objective.associations.action.color,
			colorIndicators: objective.associations.indicator.color,
			colorProjects: objective.associations.project.color,
			calculatePercentage: objective.mea003Calcperc,
			percentageActions: objective.associations.action.percentage,
			percentageIndicators: objective.associations.indicator.percentage,
			percentageProjects: objective.associations.project.percentage
		});
	};

	const onMouseOutObjective = () => {
		setTooltipValue(undefined);
	};

	const onCallbackDeleteMap = (mapId: number) => {
		setCards(cards.filter((x) => x.id !== mapId));
		setMapSelected(mapSelected.mea001Id === mapId ? {} as IMetba001001 : mapSelected);
	};

	const onCallbackDeletePerspective = (id: number, name: string) => {	
		confirm(
			translate("Exclusão da Perspectiva"), 
			`${translate("Deseja realmente excluir a perspectiva")}: ${name} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Perspective/Delete",
					urlParameters: {
						id,
						title: name,
						mapId: mapSelected.mea001Id,
						mapTitle: mapSelected.mea001Nome
					}
				});
				addToast({
					color: "green",
					description: translate("Perspectiva excluída com sucesso"),
					title: translate("Sucesso")
				});
	
				setPerspectives((prevState) => prevState.filter((perspective) => perspective.mea002Id !== id));
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir perspectiva"), addToast);
			},
			true
		);
	};

	const onCallbackDeleteObjective = (id: number, name: string, perspectiveName: string) => {	
		confirm(
			translate("Exclusão de Objetivo"), 
			`${translate("Deseja realmente excluir o objetivo")}: ${name} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Objectives/Delete",
					urlParameters: {
						id,
						title: name,
						mapId: mapSelected.mea001Id,
						perspectiveName
					}
				});
				addToast({
					color: "green",
					description: translate("Objetivo excluído com sucesso"),
					title: translate("Sucesso")
				});
	
				onCallbackMap();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Objetivo"), addToast);
			},
			true
		);
	};

	const onCallbackChangeObjective = (objectiveEdit: IMetba003) => {
		setPerspectives((prevState) => {
			const updateState = prevState.map((perspective) => {
				if(perspective.mea002Id === objectiveEdit.mea003Mea002Id){
					return {
						...perspective,
						metba003: perspective.metba003 ? perspective.metba003.map((objective: any) => {
							if(objective.mea003Id === Number(objectiveEdit.mea003Id)){
								return{
									...objectiveEdit
								};
							}
							return objective;
						}) : []
					};
				}
				return perspective;
			});
			return updateState;
		});
	};

	const updateObjectiveInPerspective = (add: boolean, perspectiveId: number, objectiveEdit?: IMetba003) => {
		if(objectiveEdit === undefined)
			return;

		if(add){
			setPerspectives((prevState) => {
				const updateState = prevState.map((perspective) => {
					if(perspective.mea002Id === perspectiveId){

						const newObjective = {
							...objectiveEdit,
							mea003Mea002Id: perspectiveId
						};

						return {
							...perspective,
							metba003: perspective.metba003 ? [...perspective.metba003, newObjective] : [newObjective]
						};
					}
					return perspective;
				});
				return updateState;
			});

			return;
		}
		
		setPerspectives((prevState) => {
			const updateState = prevState.map((perspective) => {
				if(perspective.mea002Id === perspectiveId){
					return {
						...perspective,
						metba003: perspective.metba003?.filter((x) => x.mea003Id !== objectiveEdit.mea003Id)
					};
				}
				return perspective;
			});
			return updateState;
		});
	};

	const onCallbackRelateEffect = (objectiveEdit: IMetba003) => {

		if(relateEffect1 === undefined){
			setRelateEffect1(objectiveEdit);
			
			const state = {
				...objectiveEdit,
				emphasis: true
			};

			setInEditCauseEffect(true);
			onCallbackChangeObjective(state);

			addToast({
				color: "blue",
				description: translate("Agora selecione outro objetivo"),
				title: translate("Informação")
			});

			return;
		}
		
		if(relateEffect1.mea003Id === objectiveEdit.mea003Id){
			const state = {
				...objectiveEdit,
				emphasis: false
			};

			setRelateEffect1(undefined);
			setInEditCauseEffect(false);
			onCallbackChangeObjective(state);

			addToast({
				color: "yellow",
				description: translate("Causa/Efeito cancelado"),
				title: translate("Atenção")
			});

			return;
		}
		
		if(isLowerLevelPerspective(relateEffect1, objectiveEdit)){
			addToast({
				color: "yellow",
				description: translate("Não é possível criar um relacionamento Causa x Efeito para uma perspectiva de ordem inferior"),
				title: translate("Atenção")
			});
			return;
		}

		setRelateEffect2(objectiveEdit);
	};

	const isLowerLevelPerspective = (objective1: IMetba003, objective2: IMetba003) => {
		const perspective1 = perspectives.find((objctive) => objctive.mea002Id === objective1.mea003Mea002Id);
		const perspective2 = perspectives.find((objctive) => objctive.mea002Id === objective2.mea003Mea002Id);

		if(perspective1 && perspective2 && perspective2.mea002Ordem > perspective1.mea002Ordem){
			return true;
		}
		return false;
	};

	useEffect(() => {
		if(showCauseEffect === false){
			const objective = relateEffect1 as IMetba003;
			const state = {
				...objective,
				emphasis: false
			};
			onCallbackChangeObjective(state);
			setRelateEffect1(undefined);
			setInEditCauseEffect(false);
		}
	}, [showCauseEffect]);

	useEffect(() => {
		if(relateEffect1 !== undefined && relateEffect2 !== undefined){
			submitAddCauseEffect(relateEffect1, relateEffect2);
		}
	}, [relateEffect2]);

	const submitAddCauseEffect = async (cause: IMetba003,  effect: IMetba003) => {
		try{
			const {data} = await RCCServicesApi.postRequest({
				method: "Maps/InsertCauseEffect",
				urlParameters: {
					idMap: mapSelected.mea001Id,
					idCause: cause.mea003Id,
					idEffect: effect.mea003Id
				}
			});

			const newCauseEffect = {
				mea005Id: data, 
				mea005Mea003Id: cause.mea003Id, 
				mea005Mea003Id2: effect.mea003Id
			};

			const prev = {
				...cause,
				metba005: cause.metba005 ? [...cause.metba005, newCauseEffect] : [newCauseEffect]
			};

			setInEditCauseEffect(false);
			setRelateEffect1(undefined);
			setRelateEffect2(undefined);
			onCallbackChangeObjective(prev);

			onCallbackMap();

			addToast({
				color: "green",
				description: translate("Causa/Efeito adicionado com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			setInEditCauseEffect(false);
			setRelateEffect1(undefined);
			setRelateEffect2(undefined);
			standardErrorMessages(err, translate("Erro ao adicionar a Causa/Efeito"), addToast);
		}
	};

	const onCallbackDeleteCauseEffect = (objectiveId: number, causeEffectId: number) => {
		setPerspectives((prevState) => {
			const updateState = prevState.map((perspective) => {
				if(perspective.metba003 && perspective.metba003.find((x) => x.mea003Id === objectiveId)){
					return {
						...perspective,
						metba003: perspective.metba003 ? perspective.metba003.map((objective: any) => {
							if(objective.mea003Id === objectiveId){
								return{
									...objective,
									metba005: objective.metba005 ? objective.metba005.filter((z: any) => z.mea005Id !== causeEffectId) : objective.metba005
								};
							}
							return objective;
						}) : []
					};
				}
				return perspective;
			});
			return updateState;
		});

		onCallbackMap();
	};

	const updateOrderObjectives = (perspectiveId: number, objectivesList: IMetba003[]) => {
		setPerspectives((prevState) => {
			const updateState = prevState.map((perspective) => {
				if(perspective.mea002Id === perspectiveId){
					return {
						...perspective,
						metba003: objectivesList
					};
				}
				return perspective;
			});
			return updateState;
		});
	};

	const onCallbackAddMap = async (map: number) => {
		await getMaps();
		await onClickCardFuncion(map);
	};

	const contentMapsLayout = (stateLoading: boolean, stateLoadingError: boolean, stateContent:any, nodeError: React.ReactNode, nodeEmpty:React.ReactNode, nodeContent: React.ReactNode) => (
		
		stateLoading 
			? (<Loader/>) 
			: (<>
				{stateLoadingError 
					? nodeError 
					: (<> {stateContent.mea001Id !== undefined ? nodeContent : nodeEmpty }</>)
				}
			</>)
	);

	const handleDragStart = (activeIdX: string) => {
		const activeType = activeIdX.substring(0,1);
		const activeId = activeIdX.substring(2);

		if(activeType === "o"){
			const activeObjective = objectives.find((objective) => objective.id === activeId);
			setCardDragOver(activeObjective?.mea003Nome);
			return;
		}

		if(activeType === "p"){
			const activePerspective = perspectives.find((perspective) => perspective.id === activeId);
			setCardDragOver(activePerspective?.mea002Nome);
		}
	};

	const handleDragOver = (activeIdX: string, overIdX: string) => {
		const activeType = activeIdX.substring(0,1);
		const overType = overIdX.substring(0,1);

		const activeId = activeIdX.substring(2);
		const overId = overIdX.substring(2);


		if(activeType === "p" && overType === "p"){
			handleDragPerspective(activeId, overId);
			return;
		}

		if(activeType === "o" && overType === "o"){
			handleDragObjective(activeId, overId, false);
			return;
		}

		if(activeType === "o" && overType === "p"){
			handleDragObjective(activeId, overId, true);
		}
	};

	const hasCauseEffectInObjective = (objective: IMetba003) => {
		if(objective && objective.metba005)
			return objective.metba005.length > 0;

		return false;
	};
	const hasCauseEffectInPerspective = (perspective: IMetba002) => perspective.metba003?.some((x) => x.metba005?.length);

	const handleDragPerspective = (activeId: string, overId: string) => {
		const perspectiveActive = perspectives.find((perspective) => perspective.id === activeId);
		if(perspectiveActive && hasCauseEffectInPerspective(perspectiveActive)){
			addToast({
				color: "yellow",
				description: translate("Não é possivel mover perspectiva que possui Causa/Efeito"),
				title: translate("Atenção")
			});
			return;
		}

		const activeIndex = perspectives.findIndex((perspective) => perspective.id === activeId);
		const overIndex = perspectives.findIndex((perspective) => perspective.id === overId);

		const oldPerspectives = perspectives;
		let updatePerspectives = perspectives;
		updatePerspectives = arrayMove(updatePerspectives, 
			activeIndex, 
			overIndex);

		setPerspectives(updatePerspectives);
		submitOrderPerspective(updatePerspectives, oldPerspectives);
	};

	const handleDragObjective = (activeId: string, overId: string, overPerspective: boolean) => {
		const activeObjective = objectives.find((objective) => objective.id === activeId);
		
		if(activeObjective && hasCauseEffectInObjective(activeObjective)){
			addToast({
				color: "yellow",
				description: translate("Não é possivel mover objetivo que possui Causa/Efeito"),
				title: translate("Atenção")
			});
			return;
		}
		
		if(overPerspective){
			dragOrderObjectivesInPerspectivesDifferent(activeId, overId);
			return;
		}

		const overObjective = objectives.find((objective) => objective.id === overId);
		if(activeObjective?.mea003Mea002Id !== overObjective?.mea003Mea002Id){
			dragOrderObjectivesInPerspectivesDifferent(activeId, String(overObjective?.mea003Mea002Id));
			return;
		}
		
		dragOrderObjectivesInPerspectivesEqual(activeId, overId);
	};

	const dragOrderObjectivesInPerspectivesEqual = (activeId: string, overId: string) => {
		const activeObjective = objectives.find((objective) => objective.id === activeId);
		const perspectiveId = activeObjective?.mea003Mea002Id || 0;
		
		const activePerspective = perspectives.find((perspective) => perspective.mea002Id === activeObjective?.mea003Mea002Id);
		const perspectiveName = activePerspective?.mea002Nome || "";
		const objectivesActivePerspective = activePerspective?.metba003 ? activePerspective.metba003 : [] as IMetba003[];

		const activeIndex = objectivesActivePerspective.findIndex((objective) => objective.id === activeId);
		const overIndex = objectivesActivePerspective.findIndex((objective) => objective.id === overId);

		const oldObjectives = objectivesActivePerspective;
		let updateObjectives = objectivesActivePerspective;
		updateObjectives = arrayMove(updateObjectives, 
			activeIndex, 
			overIndex);

		const updateObjectivesFilter = updateObjectives.filter((x) => x !== undefined);
		updateOrderObjectives(perspectiveId, updateObjectivesFilter);
		submitOrderObjective(perspectiveId, perspectiveName, updateObjectivesFilter, oldObjectives);
	};

	const dragOrderObjectivesInPerspectivesDifferent = (activeId: string, overId: string) => {
		const activeObjective = objectives.find((objective) => objective.id === activeId);
		const activePerspective = perspectives.find((perspective) => perspective.mea002Id === activeObjective?.mea003Mea002Id);
		const overPerspective = perspectives.find((perspective) => perspective.id === overId);

		updateObjectiveInPerspective(false, activePerspective?.mea002Id || 0, activeObjective);
		updateObjectiveInPerspective(true, overPerspective?.mea002Id || 0, activeObjective);
		submitPerpectiveOfObjective(activeObjective?.mea003Id || 0, activeObjective?.mea003Nome || "", overPerspective?.mea002Id || 0, overPerspective?.mea002Nome || "");
	};

	async function submitOrderPerspective(updatePerspectives: IMetba002[], oldPerspectives: IMetba002[]){
		try{
			await RCCServicesApi.putRequest({
				method: "Perspective/UpdateOrder",
				body: {
					perspectivas: updatePerspectives.map((x:IMetba002, index) => ({
						mea002Id: x.mea002Id,
						mea002Ordem: index+1
					})),
					mea001Id: mapSelected.mea001Id,
					mea001Nome: mapSelected.mea001Nome
				}
			});
		}catch(err){
			// rollback
			setPerspectives(oldPerspectives);
			standardErrorMessages(err, translate("Erro ao atualizar a ordenação"), addToast);
		}
	}

	async function submitOrderObjective(perspectiveId: number, perspectiveName: string, updateObjectives: IMetba003[], oldObjectives: IMetba003[]){
		try{
			await RCCServicesApi.putRequest({
				method: "Objectives/UpdateOrder",
				body: {
					mapId: mapSelected.mea001Id,
					perspectiveName,
					objectives: updateObjectives.map((x:IMetba003, index) => ({
						mea003Id: x.mea003Id,
						mea003mea002Id: x.mea003Mea002Id,
						mea003Ordem: index+1
					}))
				}
			});

		}catch(err){
			// rollback
			updateOrderObjectives(perspectiveId, oldObjectives);
			standardErrorMessages(err, translate("Erro ao atualizar a ordenação"), addToast);
		}
	}

	async function submitPerpectiveOfObjective(objectiveId: number, objectiveName: string, newPerspectiveId: number, newPerspectiveTitle: string){
		try{
			await RCCServicesApi.putRequest({
				method: "Objectives/UpdatePerpectiveOfObjective",
				urlParameters: {
					objectiveId,
					objectiveName,
					newPerspectiveId,
					newPerspectiveTitle,
					mapId: mapSelected.mea001Id
				}
			});

			onCallbackMap();
			
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar a ordenação"), addToast);
		}
	}

	const onCallbackMap = async () => {
		setUpdatingPercentage(true);

		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/SelectMapPerspectiveObjective",
				urlParameters: {id: mapSelected.mea001Id}
			});

			setPerspectives(result.data.metba002);
			setUpdatingPercentage(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar detalhes do mapa"), addToast);
			setUpdatingPercentage(false);
		}
	};

	const handleCardDragOver = (objectiveTitle: string) => (
		<ObjectiveCardBasic>
			<div className="container-card">
				<RCCGrid 
					container
					className="card-header"
					justifyContent="space-between"
				>
					<h5>
						<RCCGrid styleInline="display: flex">
							<WrapText lineClamp={2}>
								{objectiveTitle}
							</WrapText>
						</RCCGrid>
					</h5>
				</RCCGrid>
			</div>	
		</ObjectiveCardBasic>
	);

	const contentMap = () => (
		<>
			{
				contentMapsLayout(
					loadingMapContent, 
					errorLoadingMapContent, 
					mapSelected, 
					<RCCAlert 
						title={translate("Atenção")}
						description={translate("Erro ao buscar detalhes do mapa")}
					/>,
					<RCCAlert
						title={translate("Nenhum mapa selecionado")}
						description={translate("Selecione um mapa para ver seus detalhes")}
					/>,
					<MapContent openDetails={openDetails} color={mapSelected.color || ""}>
						<div className="mapa-header">
							<RCCGrid
								container
							>
								<RCCGrid 
									container
									item
									alignItems="center"
									justifyContent="space-between"
									styleInline="margin-bottom: 10px !important"
								>
									<RCCGrid 
										item
										xs={6}
										md={6}
										lg={9}
									>
										<Title color="purple" size="xl">
											<WrapText>
												{mapSelected.mea001Nome}
											</WrapText>
										</Title>
										<p className="description">
											<strong>{translate("Descrição")}: </strong>
											<WrapText>
												{mapSelected.mea001Descricao}
											</WrapText>
										</p>
									</RCCGrid>
									<RCCGrid 
										styleInline="display: flex; min-width: 250px;"
									>
										<RCCTooltip
											position="bottom"
											text={translate("Detalhes")}
											color={openDetails ? "blue" : "grey"}
										>
											<RCCButton
												icon={{name: "list", color: "#FFF", size: 16}}
												color={openDetails ? "blue" : "grey"}
												layout="isRounded"
												size="md"
												onClick={() => setOpenDetails(!openDetails)}
											/>
										</RCCTooltip>
										<RCCTooltip
											position="bottom"
											text={showCauseEffect ? translate("Ocultar Causa x Efeito") : translate("Mostrar Causa x Efeito")}
											color={showCauseEffect ? "blue" : "grey"}
										>
											<RCCButton
												icon={{
													name: showCauseEffect ? "eye" : "eyeSlash",
													color: "#FFF",
													size: 16}
												}
												color={showCauseEffect ? "blue" : "grey"}
												layout="isRounded"
												size="md"
												onClick={() => setShowCauseEffect(!showCauseEffect)}
											/>
										</RCCTooltip>
										<RCCTooltip
											position="bottom"
											text={translate("Orçamento")}
											color="grey"
										>
											<RCCButton
												icon={{name: "dollar", color: "#FFF", size: 16}}
												color="grey"
												layout="isRounded"
												size="md"
												onClick={(e) => {e.stopPropagation(); showBudget(mapSelected.mea001Nome, mapSelected.mea001Id);}}
											/>
										</RCCTooltip>
										
										<RCCTooltip
											position="bottom"
											text={translate("Estratégias")}
											color="grey"
										>
											<RCCButton
												icon={{name: "chessKing", color: "#FFF", size: 16}}
												color="grey"
												layout="isRounded"
												size="md"
												onClick={() => window.open(`/strategies/panel/?map=${mapSelected.mea001Id}`)}
											/>
										</RCCTooltip>
										
										<RCCTooltip
											position="bottom"
											text={translate("Imprimir")}
											color="grey"
										>
											<RCCButton
												icon={{name: "print", color: "#FFF", size: 16}}
												color="grey"
												layout="isRounded"
												size="md"
												onClick={() => window.open(`/maps/print/${mapSelected.mea001Id}`)}
											/>
										</RCCTooltip>

										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											{(mapSelected.mea008Permissao >= 2 || administrator || superUser) && (
												<RCCTooltip
													position="left"
													text={translate("Nova Perspectiva")}
													color="purple"
												>
													<RCCButton
														icon={{name: "plus", color: "#FFF", size: 18}}
														color="purple"
														layout="isRounded"
														onClick={() => {refModalsPerspective.current?.openModal();}}
														size="md"
													/>
												</RCCTooltip>
											)}
										</CheckPermission>

									</RCCGrid>
								</RCCGrid>
										
								<RCCGrid
									container
									className="mapa-header-info"
								>
									<div className="mapa-header-details">
										<p>
											<strong>{translate("Missão")}: </strong>
											<span>{mapSelected.mea001Missao}</span>
										</p>
										<p>
											<strong>{translate("Visão")}: </strong>
											<span>{mapSelected.mea001Visao}</span>
										</p>
										<p>
											<strong>{translate("Valores")}: </strong>
											<span>{mapSelected.mea001Valores}</span>
										</p>							
									</div>
									
								</RCCGrid>
								<RCCGrid 
									container
									justifyContent="flex-end"
								>
									<RCCGrid className="gauge-map" styleInline="min-height: 50px">
										<>
											<RCCGrid 
												container 
												justifyContent="center"
											>
												{
													!updatingPercentage ? 
														<h3>{translate("Realizado")}: {formatNumber(mapSelected.realizedPercentage, currentLanguage, 2)}%</h3>
														:
														<h3>{translate("Atualizando...")}</h3>
												}
														
											</RCCGrid>
											<BadgeAssociate
												mapId={mapSelected.mea001Id}
												associations={mapSelected.associations}
												position="center"
												label="word"
												color="grey"												
											/> 
										</>

									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						</div>
						<div className="mapa-body">
							<SortableContext 
								onDragStart={handleDragStart}
								onDragOver={handleDragOver}
								onDragEnd={() => {}}
							>
								<PerspectiveList 
									perspectives={perspectives} 
								/>

								<div style={{ listStyle: "none" }}>
									{
										cardDragOver && 
										<DragOverlay>
											{handleCardDragOver(cardDragOver)}
										</DragOverlay>
									}
									
								</div>
							</SortableContext>
						</div>
					</MapContent>
				)
			}
		</>
	);

	async function getMap(idMap: number){
		setErrorLoadingMapContent(false);
		setLoadingMapContent(true);
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/SelectMapPerspectiveObjective",
				urlParameters: {id: idMap}
			});

			setMapSelected(result.data);
			setPerspectives(result.data.metba002);
			setLoadingMapContent(false);
			setShowCauseEffect(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar detalhes do mapa"), addToast);
			setLoadingMapContent(false);
			setErrorLoadingMapContent(true);
		}
	}
	
	const onClickCardFuncion = async (idMap: number) => {
		await getMap(idMap);
		refSidebarCard.current?.changeListVisibility();
	};

	async function getMaps(idMap?: number){
		setLoading(true);
		setErrorLoadingMaps(false);
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/List",
				urlParameters: {
					f: filters.f || "",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : "",
					active: filters.active ? filters.active : "S",
					tags: filters.tags || ""
				}
			});
			
			setCards(result.data.map((x: any) => ({
				id: x.mea001Id,
				title: x.mea001Nome,
				subtitle: mapsSubtitle(x.mea001Dtvalini, x.mea001Dtvalfim),
				filtered: true,
				selected: false,
				favorite: x.favorite,
				active: x.mea001Ativo === "S",
				permissions: x.mea008Permissao
			})));

			if(idMap){
				const newMapId = typeof idMap === "number" ? idMap : parseInt(idMap, 10);
				const map = result.data.find((x: any) => x.mea001Id === newMapId);
				setLoading(false);
				return map;
			}if(params.mapId && params.mapId.length > 0 && result.data.find((x: any) => x.mea001Id === Number(params.mapId)))
				getMap(Number(params.mapId));

			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar mapas"), addToast);
			setLoading(false);
			setErrorLoadingMaps(true);
		}
		return null;
	}

	async function filtersMaps(data:IFiltersCard){
		setFilters(data);
	}

	useEffect(() => {
		setMapSelected({} as IMetba001001);
		getMaps();
	}, [filters]);

	useEffect(() => {
		setObjectives(perspectives.flatMap((x) => x.metba003) as IMetba003[]);
	}, [perspectives]);

	async function updateMapFavorite(cardClick: ISidebarCardItem){
		try{	
			await RCCServicesApi.putRequest({
				method: "Maps/UpdateMapFavorite",
				urlParameters: {id: cardClick.id, isFavorite: !cardClick.favorite}
			});
	
			setCards(cards.map((card) => ({ 
				...card,
				favorite: card.id === cardClick.id ? !card.favorite : false
			})));

			addToast({
				color: "green",
				description: !cardClick.favorite ? translate("Mapa favoritado com sucesso") : translate("Mapa desfavoritado com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			standardErrorMessages(err, !cardClick.favorite ? translate("Erro ao favoritar mapa") : translate("Erro ao desfavoritar mapa"), addToast);
		}
	}

	const showBudget = async (title: string, mapId: number) => {
		try{
			setLoadingBackdrop(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/Budget",
				urlParameters: {
					group: 3, // 3 - Maps
					id: mapId,
					id2: "",
					id3: "",
					id4: "",
					f: "",
					responsible: "",
					authority: "",
					sector: "",
					situation: "2,1,0",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : ""
				}
			});

			const updateLabelMonth: IIntbb001BudgetGrouper[] = data.map((dt: IIntbb001BudgetGrouper) => ({
				...dt,
				name: translate(`monthWorkspace:${dt.name}`)
			}));
			setLoadingBackdrop(false);

			const titleModal = `${translate("Mapa")}: ${title}`;
			refModalActionBudget.current?.actionButtonOpenBudget(titleModal, updateLabelMonth);

		}catch(err){
			setLoadingBackdrop(false);
			standardErrorMessages(err, translate("Erro ao carregar orçamento"), addToast);
		}
	};
 
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response ? filtersTagsRef.current?.response : "";
			return updateState;
		});
	};

	const extractSingleValueFromArrayOfObject = (array: string | undefined) => {
		if(!array)
			return undefined;
		const newArray =  array.split(",");
		if(!newArray || newArray.length === 0)
			return undefined;
		return newArray.map((x: string) => parseInt(x, 10));
	};

	return (
		<>
			<MapContextProvider.Provider
				value={{
					mapId: mapSelected.mea001Id || 0,
					permissions: mapSelected.mea008Permissao,
					showCauseEffect,
					inEditCauseEffect,
					updatingPercentage,
					onCallbackRelateEffect,
					onCallbackDeleteCauseEffect,
					onCallbackDeletePerspective,
					onCallbackDeleteObjective,
					onMouseOverObjective,
    				onMouseOutObjective,
					openModalPerspective: refModalsPerspective.current?.openModal,
					openModalObjective: refModalsObjectives.current?.openModal,
					openModalListIniciatives: refModalIniciatives.current?.openModalIniciative
				}}
			>

				{
					loading ? (
						<Loader/>) : 
						(
							<>
								{errorLoadingMaps ? (
									<RCCAlert 
										title={translate("Atenção")}
										description={translate("Erro ao buscar mapas")}
									/>
								) : (
									<>
										<SidebarCard 
											ref={refSidebarCard}
											title={translate("Mapa Estratégico")} 
											icon={() => <AppIcons name="strategicPlanning" />}
											defaultCardSelected={params.mapId && params.mapId.length > 0 ? Number(params.mapId) : undefined}
											content={contentMap()}
											customCards={
												<>
													{!cards || cards.length === 0 ? (
														<RCCAlert
															description={translate("Não foram encontrados dados")}
														/>
													): (
														<SidebarMaps
															items={cards}
															setItems={setCards}
															refModalMap={refModalsMap}
															onClickCardFuncion={onClickCardFuncion}
															selectedMap={mapSelected?.mea001Id}
															updateMapFavorite={updateMapFavorite}
														/>
													)}
												</>
											}
											filterContent={
												<>
													<SidebarCardFilters 
														filtersFunction={filtersMaps}
														handleSubmit={handleSubmitFilters}
														reset={resetFilters}
														control={controlFilters}
														register={registerFilters}
														filters={filters}
														showFilters={showFilters}
													/>
													<FiltersTagsContainer 
														ref={filtersTagsRef}
														onSubmitFilters={submitFiltersTags}
														tagType={TagsTypes.MAPS}
														showFilters={showTagsFilters}
														defaultValue={extractSingleValueFromArrayOfObject(filters.tags)}
													/>
												</>
											}
											buttonsContent={
												<>
													<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
														<RCCTooltip
															text={translate("Novo Mapa")}
															color="purple"
														>
															<RCCButton
																icon={{name: "plus", color: "#FFF", size: 18}}
																color="purple"
																layout="isRounded"
																onClick={() => refModalsMap.current?.actionButtonAdd()}
																size="md"
															/>
														</RCCTooltip>
													</CheckPermission>
													<Reports grouper={ReportGroupers.STRATEGIC_MAPS} />
													<RCCTooltip
														text={translate("Tags")}
														color={showTagsFilters || filters.tags ? "blue" : "grey"}
													>
														<RCCButton
															icon={{name: "tags", color: "#FFF", size: 16}}
															color={showTagsFilters || filters.tags ? "blue" : "grey"}
															layout="isRounded"
															size="md"
															onClick={() => setShowTagsFilters((prevState) => !prevState)}
														/>
													</RCCTooltip>
													<RCCTooltip
														text={translate("Filtros")}
														color={showFilters || filters.f || filters.active !== "T" || filters.endDate || filters.startDate ? "blue" : "grey"}
													>
														<RCCButton
															icon={{name: "filter", color: "#FFF", size: 16}}
															color={showFilters || filters.f || filters.active !== "T" || filters.endDate || filters.startDate ? "blue" : "grey"}
															layout="isRounded"
															size="md"
															onClick={() => setShowFilters((prevState) => !prevState)}
														/>
													</RCCTooltip>
												</>
											}
										/>
									</>
								)}
							</>
						)	
				}	

			</MapContextProvider.Provider>
				
			<ModalsMap 
				ref={refModalsMap}
				cards={cards}
				setCards={setCards}
				mapsSubtitle={mapsSubtitle}
				onCallbackDeleteMap={onCallbackDeleteMap}
				onCallbackMaps={onCallbackAddMap}
			/>

			<ModalPerspective 
				ref={refModalsPerspective}
				map={mapSelected}
				perspectives={perspectives} 
				setPerspectives={setPerspectives}
				onCallbackMap={() => onCallbackMap()}
			/>

			<ModalObjective
				ref={refModalsObjectives}
				onCallbackMap={() => onCallbackMap()}
			/>

			<ModalIniciatives 
				ref={refModalIniciatives}
				onCallbackIniciatives={() => getMap(mapSelected.mea001Id || 0)}
			/>

			<TooltipGaugeChartMaps 
				value={tooltipValue}
			/>

			<ModalBudget 
				ref={refModalActionBudget} 
			/>
		</>
	);
};
	
export default Maps;
